import { toast } from '@/components/ui/use-toast';
import { router } from '@/lib/router/routes';

import { MFA_REQUIRED_MESSAGE } from './mfaError';

// One MFA event yields a burst of denied responses (TanStack Query `retry: 2`
// plus parallel queries on a page). This guard collapses the burst into a
// single toast + redirect. It is re-armed once the redirect resolves so a
// later, separate MFA event still redirects.
let isRedirectingForMfa = false;

const isOnAuthPath = (): boolean =>
  window.location.pathname.startsWith('/auth/');

// On a normal protected path, preserve pathname + query string so deep policy
// URLs round-trip after re-auth. When we are already under /auth/ (e.g. the
// hard-navigation fallback fired from an auth page), omit `from` entirely to
// avoid a `from=/auth/login` self-target. The hash fragment is intentionally
// not preserved (out of scope).
const loginUrlWithFrom = (): string => {
  if (isOnAuthPath()) {
    return '/auth/login';
  }
  return `/auth/login?from=${encodeURIComponent(
    window.location.pathname + window.location.search
  )}`;
};

/**
 * Handle a request-level MFA-gate denial: notify the user and send them to the
 * login page to re-authenticate. Safe to call from non-component code (the
 * hey-api error interceptor). Deduplicated. Relies on the backend clearing the
 * session on a blocked request (DC-3455) to avoid a redirect loop — there is no
 * frontend backstop; see the plan's deployment gate.
 */
export const handleMfaRequired = (): void => {
  // Already on the login / mfa-verify page. Bailing prevents a self-redirect
  // loop on those two pages and ignores late TanStack `retry` attempts that
  // arrive after we've landed (closes the re-arm-vs-retry race). We do NOT bail
  // on every /auth/ page: /auth/forgot-password and /auth/reset-password have no
  // errorElement, so an MFA-gate denial there must still redirect.
  //
  // This URL-committed bail-out — not the `isRedirectingForMfa` boolean — is
  // the load-bearing dedupe for late TanStack-Query retries: once the URL has
  // committed to /auth/login or /auth/mfa-verify, every subsequent retry hits
  // this check and exits immediately, regardless of the boolean's state.
  const { pathname } = window.location;
  if (
    pathname.startsWith('/auth/login') ||
    pathname.startsWith('/auth/mfa-verify')
  ) {
    return;
  }
  if (isRedirectingForMfa) {
    return;
  }
  isRedirectingForMfa = true;

  toast({ title: MFA_REQUIRED_MESSAGE, variant: 'destructive' });

  // Capture the URL once so both the SPA navigate and the hard-nav fallback use
  // the same computed value (guards against a partially-committed navigation
  // changing window.location before the .catch fires).
  const loginUrl = loginUrlWithFrom();

  // `router.navigate` returns a promise in the data router; wrap defensively.
  Promise.resolve(router.navigate(loginUrl, { replace: true }))
    .catch(() => {
      // SPA navigation failed; force a hard navigation so we still leave the
      // crashed page. Otherwise the next retry would re-toast on the stale page.
      window.location.assign(loginUrl);
    })
    .finally(() => {
      isRedirectingForMfa = false;
    });
};

/**
 * Test-only: reset the module-level dedupe guard for order-independent tests.
 * Ships in the production bundle but is inert (one boolean reset, never called
 * by app code) and harmless. Left ungated — the codebase has no
 * `import.meta.env.TEST` seam convention.
 */
export const __resetMfaRedirectForTests = (): void => {
  isRedirectingForMfa = false;
};
